import Cookies from 'js-cookie';

export const REVIEW_PROMPT_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const REVIEW_PROMPT_COOKIE_PREFIX = 'coachify_review_prompt_next_show_at';
export const REVIEW_PROMPT_LOGIN_TRIGGER_PREFIX = 'coachify_review_prompt_after_login';

export const REVIEWER_ROLE_CUSTOMER = 'customer';
export const REVIEWER_ROLE_COACH = 'coach';

const REVIEWER_ROLES = [REVIEWER_ROLE_CUSTOMER, REVIEWER_ROLE_COACH];

/** Cookie lifetime — timing is enforced by stored timestamp, not expiry alone. */
const NEXT_SHOW_COOKIE_EXPIRES_DAYS = 30;

const sanitizeUserId = userId => {
  const raw = String(userId || '').trim();
  if (!raw) return '';
  return encodeURIComponent(raw);
};

const sanitizeReviewerRole = reviewerRole => {
  const role = String(reviewerRole || '').trim().toLowerCase();
  return REVIEWER_ROLES.includes(role) ? role : '';
};

export const buildReviewPromptCookieKey = ({ userId, reviewerRole }) => {
  const safeUserId = sanitizeUserId(userId);
  const safeRole = sanitizeReviewerRole(reviewerRole);
  if (!safeUserId || !safeRole) return '';
  return `${REVIEW_PROMPT_COOKIE_PREFIX}:${safeRole}:${safeUserId}`;
};

const buildLoginTriggerStorageKey = ({ userId, reviewerRole }) => {
  const safeUserId = sanitizeUserId(userId);
  const safeRole = sanitizeReviewerRole(reviewerRole);
  if (!safeUserId || !safeRole) return '';
  return `${REVIEW_PROMPT_LOGIN_TRIGGER_PREFIX}:${safeRole}:${safeUserId}`;
};

const parseTimestamp = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.nextShowAt != null) {
      const nested = Number(parsed.nextShowAt);
      return Number.isFinite(nested) ? nested : null;
    }
  } catch {
    // not JSON — fall through to numeric parse
  }
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

export const getNextReviewPromptShowAt = ({ userId, reviewerRole }) => {
  if (typeof window === 'undefined') return null;
  const key = buildReviewPromptCookieKey({ userId, reviewerRole });
  if (!key) return null;
  try {
    return parseTimestamp(Cookies.get(key));
  } catch {
    return null;
  }
};

export const setNextReviewPromptShowAt = ({ userId, reviewerRole, nextShowAt }) => {
  if (typeof window === 'undefined') return;
  const key = buildReviewPromptCookieKey({ userId, reviewerRole });
  const timestamp = Number(nextShowAt);
  if (!key || !Number.isFinite(timestamp)) return;
  try {
    Cookies.set(key, String(timestamp), {
      path: '/',
      expires: NEXT_SHOW_COOKIE_EXPIRES_DAYS,
      sameSite: 'Lax',
    });
  } catch {
    // ignore private mode / blocked cookies
  }
};

export const clearNextReviewPromptShowAt = ({ userId, reviewerRole }) => {
  if (typeof window === 'undefined') return;
  const key = buildReviewPromptCookieKey({ userId, reviewerRole });
  if (!key) return;
  try {
    Cookies.remove(key, { path: '/' });
  } catch {
    // ignore
  }
};

export const isReviewPromptDue = ({ userId, reviewerRole, now = Date.now() }) => {
  const nextShowAt = getNextReviewPromptShowAt({ userId, reviewerRole });
  if (nextShowAt == null) return true;
  return now >= nextShowAt;
};

export const setReviewPromptLoginTrigger = ({ userId, reviewerRole }) => {
  if (typeof window === 'undefined') return;
  const key = buildLoginTriggerStorageKey({ userId, reviewerRole });
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, 'true');
  } catch {
    // ignore quota / private mode
  }
};

export const hasReviewPromptLoginTrigger = ({ userId, reviewerRole }) => {
  if (typeof window === 'undefined') return false;
  const key = buildLoginTriggerStorageKey({ userId, reviewerRole });
  if (!key) return false;
  try {
    return window.sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

export const consumeReviewPromptLoginTrigger = ({ userId, reviewerRole }) => {
  if (typeof window === 'undefined') return;
  const key = buildLoginTriggerStorageKey({ userId, reviewerRole });
  if (!key) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export const clearReviewPromptLoginTriggers = userId => {
  if (typeof window === 'undefined' || !userId) return;
  REVIEWER_ROLES.forEach(reviewerRole => {
    consumeReviewPromptLoginTrigger({ userId, reviewerRole });
  });
};

/** Set fresh-login triggers for both reviewer roles after a successful login. */
export const markReviewPromptFreshLogin = userId => {
  if (!userId) return;
  REVIEWER_ROLES.forEach(reviewerRole => {
    setReviewPromptLoginTrigger({ userId, reviewerRole });
  });
};

export const shouldAllowAutomaticReviewPromptCheck = ({ userId, reviewerRole, now = Date.now() }) => {
  if (!userId || !sanitizeReviewerRole(reviewerRole)) return false;
  if (hasReviewPromptLoginTrigger({ userId, reviewerRole })) return true;
  return isReviewPromptDue({ userId, reviewerRole, now });
};

export const recordAutomaticReviewPromptShown = ({ userId, reviewerRole, now = Date.now() }) => {
  setNextReviewPromptShowAt({
    userId,
    reviewerRole,
    nextShowAt: now + REVIEW_PROMPT_INTERVAL_MS,
  });
};

export const completeAutomaticReviewPromptCheck = ({ userId, reviewerRoles = REVIEWER_ROLES }) => {
  if (!userId) return;
  reviewerRoles.forEach(reviewerRole => {
    consumeReviewPromptLoginTrigger({ userId, reviewerRole });
  });
};

export const pickOldestReviewOpportunity = opportunities => {
  const list = Array.isArray(opportunities) ? opportunities.filter(o => o?.transactionId) : [];
  if (!list.length) return null;
  return [...list].sort((a, b) => {
    const at = a?.sessionDate ? new Date(a.sessionDate).getTime() : Number.POSITIVE_INFINITY;
    const bt = b?.sessionDate ? new Date(b.sessionDate).getTime() : Number.POSITIVE_INFINITY;
    return at - bt;
  })[0];
};

export const filterOpportunitiesForAutomaticPopup = (opportunities, { userId, now = Date.now() }) => {
  if (!userId) return [];
  return (Array.isArray(opportunities) ? opportunities : []).filter(opp => {
    const role = opp?.reviewerRole || REVIEWER_ROLE_CUSTOMER;
    return shouldAllowAutomaticReviewPromptCheck({ userId, reviewerRole: role, now });
  });
};

export const sortReviewOpportunitiesOldestFirst = opportunities => {
  const list = Array.isArray(opportunities) ? opportunities.filter(o => o?.transactionId) : [];
  return [...list].sort((a, b) => {
    const at = a?.sessionDate ? new Date(a.sessionDate).getTime() : Number.POSITIVE_INFINITY;
    const bt = b?.sessionDate ? new Date(b.sessionDate).getTime() : Number.POSITIVE_INFINITY;
    return at - bt;
  });
};

/** All eligible pending opportunities for one automatic popup (carousel), oldest first. */
export const getAutomaticPopupOpportunities = (opportunities, { userId, now = Date.now() }) => {
  const eligible = filterOpportunitiesForAutomaticPopup(opportunities, { userId, now });
  return sortReviewOpportunitiesOldestFirst(eligible);
};

/** @deprecated Prefer getAutomaticPopupOpportunities — kept for callers that need a single pick. */
export const selectAutomaticReviewOpportunity = (opportunities, { userId, now = Date.now() }) => {
  return pickOldestReviewOpportunity(
    filterOpportunitiesForAutomaticPopup(opportunities, { userId, now })
  );
};

export const recordAutomaticReviewPromptShownForOpportunities = ({
  userId,
  opportunities,
  now = Date.now(),
}) => {
  if (!userId) return;
  const roles = new Set(
    (Array.isArray(opportunities) ? opportunities : [])
      .map(o => sanitizeReviewerRole(o?.reviewerRole) || REVIEWER_ROLE_CUSTOMER)
      .filter(Boolean)
  );
  roles.forEach(reviewerRole => {
    recordAutomaticReviewPromptShown({ userId, reviewerRole, now });
  });
};
